import React, { useState } from 'react';
import { FaList, FaTable, FaCalendar, FaUser, FaSearch, FaPlus, FaTimes, FaClock, FaUsers, FaCompass, FaTrash, FaChevronUp, FaChevronDown, FaEdit, FaQuestionCircle, FaCheck, FaStar, FaRegStar } from 'react-icons/fa';
import PortalBackground from '../components/PortalBackground';

const UpcomingEvents = () => {
  const [viewMode, setViewMode] = useState('list');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedPresenter, setSelectedPresenter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [newEvent, setNewEvent] = useState({
    eventType: '',
    presenter: '',
    date: '',
    time: '',
    audience: 'all',
    releaseDate: '',
    isFeatured: false,
  });
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState('details');

  // Sample data - replace with actual data
  const events = [
    {
      id: 1,
      title: 'Q1 Sales Review',
      description: 'Quarterly review of sales performance and strategy planning',
      presenter: 'John Smith',
      audience: 'Sales Team',
      agenda: [
        { description: 'Q1 Performance Review', minutes: 30, time: '30 minutes' },
        { description: 'Strategy Discussion', minutes: 45, time: '45 minutes' },
        { description: 'Q2 Planning', minutes: 45, time: '45 minutes' }
      ],
      navigator: 'Sarah Johnson',
      category: 'sales',
      date: new Date(2024, 2, 26, 10, 0),
      endDate: new Date(2024, 2, 26, 12, 0),
    },
    {
      id: 2,
      title: 'Marketing Strategy Meeting',
      description: 'Planning next quarter\'s marketing campaigns',
      presenter: 'Jane Doe',
      audience: 'Marketing Team',
      agenda: [
        { description: 'Campaign Review', minutes: 30, time: '30 minutes' },
        { description: 'Budget Planning', minutes: 30, time: '30 minutes' },
        { description: 'New Initiatives', minutes: 30, time: '30 minutes' }
      ],
      navigator: 'Mike Wilson',
      category: 'marketing',
      date: new Date(2024, 2, 27, 14, 0),
      endDate: new Date(2024, 2, 27, 15, 30),
    },
  ];

  const categories = [
    { id: 'all', label: 'All' },
    { id: 'sales', label: 'Sales' },
    { id: 'operations', label: 'Operations' },
    { id: 'technology', label: 'Technology' },
    { id: 'finance', label: 'Finance' },
    { id: 'marketing', label: 'Marketing' },
    { id: 'guest', label: 'Guest' },
    { id: 'other', label: 'Other' },
  ];

  // Get unique presenters for the filter
  const presenters = ['all', ...new Set(events.map(event => event.presenter))];

  const filteredEvents = events.filter(event => {
    if (selectedCategory !== 'all' && event.category !== selectedCategory) return false;
    if (selectedPresenter !== 'all' && event.presenter !== selectedPresenter) return false;
    if (searchQuery && !event.title.toLowerCase().includes(searchQuery.toLowerCase()) && 
        !event.description.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    // Add date range filtering logic here
    return true;
  });

  const ListView = () => (
    <div className="space-y-4">
      {filteredEvents.map(event => (
        <div key={event.id} className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 hover:shadow-md transition-shadow">
          <div className="flex justify-between items-start">
            <div>
              <div className="flex items-center space-x-2">
                <h3 className="text-xl font-semibold text-gray-800">{event.title}</h3>
                {event.isFeatured && (
                  <FaStar className="text-yellow-400" />
                )}
              </div>
              <p className="text-gray-600 mt-1">{event.description}</p>
            </div>
            <span className="px-3 py-1 text-sm rounded-full bg-red-100 text-red-800">
              {event.category}
            </span>
          </div>
          
          <div className="mt-4 grid grid-cols-3 gap-4">
            <div className="flex items-center space-x-2">
              <FaCalendar className="text-gray-400" />
              <span className="text-gray-600">
                {event.date.toLocaleDateString()} {event.date.toLocaleTimeString()}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <FaUser className="text-gray-400" />
              <span className="text-gray-600">{event.presenter}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const TableView = () => (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Event</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Presenter</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Audience</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Navigator</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Released</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {filteredEvents.map(event => (
            <tr key={event.id} className="hover:bg-gray-50">
              <td className="px-6 py-4">
                <div>
                  <div className="text-sm font-medium text-gray-900">{event.title}</div>
                  <div className="text-sm text-gray-500">{event.description}</div>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {event.date.toLocaleDateString()} {event.date.toLocaleTimeString()}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{event.presenter}</td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{event.audience}</td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{event.navigator}</td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {event.releaseDate ? event.releaseDate.toLocaleDateString() : 'Not Released'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const handleCreateEvent = (e) => {
    e.preventDefault();
    // Handle event creation logic here
    console.log('New event:', newEvent);
    setShowModal(false);
    setNewEvent({
      eventType: '',
      presenter: '',
      date: '',
      time: '',
      audience: 'all',
      releaseDate: '',
      isFeatured: false,
    });
  };

  const handleTimeChange = (index, value) => {
    const newEvent = { ...selectedEvent };
    const minutes = parseInt(value) || 0;
    if (minutes > 240) minutes = 240;
    if (minutes < 0) minutes = 0;
    newEvent.agenda[index] = { ...newEvent.agenda[index], minutes };
    newEvent.agenda[index].time = `${minutes} minutes`;
    setSelectedEvent(newEvent);
  };

  return (
    <div className="min-h-screen p-6 relative">
      <PortalBackground />
      <div className="relative">
      <h1 className="text-3xl font-bold tracking-tight mb-6 text-center text-gray-900 dark:text-white">Upcoming Events</h1>

      {/* Filters Row */}
      <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-4 mb-6">
        <div className="flex flex-wrap items-center gap-4">
          {/* Search Bar */}
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search events..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Type Dropdown */}
          <div className="min-w-[150px]">
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
            >
              {categories.map(category => (
                <option key={category.id} value={category.id}>
                  {category.label}
                </option>
              ))}
            </select>
          </div>

          {/* Date Range */}
          <div className="flex gap-2 min-w-[300px]">
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
            />
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
            />
          </div>

          {/* Presenter Dropdown */}
          <div className="min-w-[150px]">
            <select
              value={selectedPresenter}
              onChange={(e) => setSelectedPresenter(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
            >
              {presenters.map(presenter => (
                <option key={presenter} value={presenter}>
                  {presenter === 'all' ? 'All Presenters' : presenter}
                </option>
              ))}
            </select>
          </div>

          {/* View Toggle */}
          <div className="flex space-x-2">
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-lg ${
                viewMode === 'list'
                  ? 'bg-red-100 text-red-600'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <FaList className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`p-2 rounded-lg ${
                viewMode === 'table'
                  ? 'bg-red-100 text-red-600'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <FaTable className="w-5 h-5" />
            </button>
          </div>

          {/* Add Event Button */}
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            <FaPlus className="w-5 h-5" />
            <span>Add Event</span>
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm">
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredEvents.map(event => (
              <div 
                key={event.id} 
                className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => {
                  setSelectedEvent(event);
                  setIsEditing(false);
                }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="mb-4">
                      <div className="flex items-center space-x-2">
                        <h3 className="text-xl font-semibold text-gray-800">{event.title}</h3>
                        {event.isFeatured && (
                          <FaStar className="text-yellow-400" />
                        )}
                      </div>
                      <p className="text-gray-600 mt-1">{event.description}</p>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="flex items-center space-x-2">
                        <FaCalendar className="text-gray-400" />
                        <span className="text-gray-600">
                          {event.date.toLocaleDateString()} {event.date.toLocaleTimeString()}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <FaUser className="text-gray-400" />
                        <span className="text-gray-600">{event.presenter}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <FaUsers className="text-gray-400" />
                        <span className="text-gray-600">{event.audience}</span>
                      </div>
                    </div>
                  </div>
                  <div className="ml-8 text-center">
                    <div className="flex items-center space-x-8">
                      <div className="text-center">
                        <div className="text-4xl font-bold text-red-600">12</div>
                        <div className="text-sm text-gray-600">Attending</div>
                      </div>
                      <div className="text-center">
                        <div className="text-4xl font-bold text-blue-600">5</div>
                        <div className="text-sm text-gray-600">Questions</div>
                      </div>
                      <div className="text-center">
                        {event.releaseDate ? (
                          <>
                            <div className="text-4xl font-bold text-green-600">
                              <FaCheck className="w-10 h-10 mx-auto" />
                            </div>
                            <div className="text-sm text-gray-600">Invitations Sent</div>
                          </>
                        ) : (
                          <>
                            <div className="text-4xl font-bold text-yellow-600">7</div>
                            <div className="text-sm text-gray-600">Days Until Public</div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Event Details Modal */}
      {selectedEvent && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center space-x-4">
                <h2 className="text-xl font-semibold">Event Details</h2>
                <div className="flex space-x-2 border-b border-gray-200">
                  <button
                    onClick={() => setActiveTab('details')}
                    className={`px-4 py-2 text-sm font-medium ${
                      activeTab === 'details'
                        ? 'text-red-600 border-b-2 border-red-600'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Event Details
                  </button>
                  <button
                    onClick={() => setActiveTab('questions')}
                    className={`px-4 py-2 text-sm font-medium ${
                      activeTab === 'questions'
                        ? 'text-red-600 border-b-2 border-red-600'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Questions
                  </button>
                </div>
              </div>
              {activeTab === 'details' && (
                <div className="flex space-x-2">
                  <button
                    onClick={() => setSelectedEvent({ ...selectedEvent, isFeatured: !selectedEvent.isFeatured })}
                    className="p-2 text-yellow-400 hover:text-yellow-500"
                  >
                    {selectedEvent.isFeatured ? (
                      <FaStar className="w-5 h-5" />
                    ) : (
                      <FaRegStar className="w-5 h-5" />
                    )}
                  </button>
                  <button
                    onClick={() => setIsEditing(!isEditing)}
                    className="p-2 text-blue-600 hover:text-blue-700"
                  >
                    <FaEdit className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => {
                      // Handle delete logic here
                      setSelectedEvent(null);
                    }}
                    className="p-2 text-red-600 hover:text-red-700"
                  >
                    <FaTrash className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setSelectedEvent(null)}
                    className="p-2 text-gray-500 hover:text-gray-700"
                  >
                    <FaTimes className="w-5 h-5" />
                  </button>
                </div>
              )}
            </div>

            {activeTab === 'details' ? (
              isEditing ? (
                <form onSubmit={(e) => {
                  e.preventDefault();
                  // Handle edit logic here
                  setIsEditing(false);
                }} className="space-y-4">
                  {/* Title */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Title
                    </label>
                    <input
                      type="text"
                      value={selectedEvent.title}
                      onChange={(e) => setSelectedEvent({ ...selectedEvent, title: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      required
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      value={selectedEvent.description}
                      onChange={(e) => setSelectedEvent({ ...selectedEvent, description: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      rows="3"
                      required
                    />
                  </div>

                  {/* Presenter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Presenter
                    </label>
                    <input
                      type="text"
                      value={selectedEvent.presenter}
                      onChange={(e) => setSelectedEvent({ ...selectedEvent, presenter: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      required
                    />
                  </div>

                  {/* Date and Time */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Date
                      </label>
                      <input
                        type="date"
                        value={selectedEvent.date}
                        onChange={(e) => setSelectedEvent({ ...selectedEvent, date: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Time
                      </label>
                      <input
                        type="time"
                        value={selectedEvent.time}
                        onChange={(e) => setSelectedEvent({ ...selectedEvent, time: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                        required
                      />
                    </div>
                  </div>

                  {/* Audience */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Audience
                    </label>
                    <select
                      value={selectedEvent.audience}
                      onChange={(e) => setSelectedEvent({ ...selectedEvent, audience: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      required
                    >
                      <option value="all">All Clients</option>
                      <option value="some">Some Clients</option>
                    </select>
                  </div>

                  {/* Agenda */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Agenda
                    </label>
                    <div className="space-y-2">
                      {selectedEvent.agenda.map((item, index) => (
                        <div key={index} className="flex gap-2 items-start">
                          <div className="flex-1">
                            <input
                              type="text"
                              value={item.description}
                              onChange={(e) => {
                                const newAgenda = [...selectedEvent.agenda];
                                newAgenda[index] = { ...newAgenda[index], description: e.target.value };
                                setSelectedEvent({ ...selectedEvent, agenda: newAgenda });
                              }}
                              placeholder="Description"
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                              required
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="relative">
                              <input
                                type="number"
                                value={item.minutes || ''}
                                onChange={(e) => handleTimeChange(index, e.target.value)}
                                min="0"
                                max="240"
                                step="5"
                                className="w-24 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                required
                              />
                            </div>
                            <span className="text-gray-600">minutes</span>
                          </div>
                          {selectedEvent.agenda.length > 1 && (
                            <button
                              type="button"
                              onClick={() => {
                                const newAgenda = selectedEvent.agenda.filter((_, i) => i !== index);
                                setSelectedEvent({ ...selectedEvent, agenda: newAgenda });
                              }}
                              className="p-2 text-red-600 hover:text-red-700"
                            >
                              <FaTrash className="w-5 h-5" />
                            </button>
                          )}
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedEvent({
                            ...selectedEvent,
                            agenda: [...selectedEvent.agenda, { description: '', minutes: '', time: '' }]
                          });
                        }}
                        className="text-red-600 hover:text-red-700 flex items-center space-x-1"
                      >
                        <FaPlus className="w-4 h-4" />
                        <span>Add Agenda Item</span>
                      </button>
                    </div>
                  </div>

                  {/* Navigator */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Navigator
                    </label>
                    <input
                      type="text"
                      value={selectedEvent.navigator}
                      onChange={(e) => setSelectedEvent({ ...selectedEvent, navigator: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      required
                    />
                  </div>

                  {/* Release Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Release Date
                    </label>
                    <input
                      type="date"
                      value={selectedEvent.releaseDate}
                      onChange={(e) => setSelectedEvent({ ...selectedEvent, releaseDate: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      required
                    />
                  </div>

                  {/* Send Invitations */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Send Invitations
                    </label>
                    <div className="flex items-center space-x-4">
                      <button
                        type="button"
                        onClick={() => {
                          // Handle sending invitations now
                        }}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                      >
                        Send Now
                      </button>
                      <div className="flex items-center space-x-2">
                        <input
                          type="number"
                          min="1"
                          className="w-20 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                          placeholder="Number"
                        />
                        <select className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent">
                          <option value="weeks">Weeks</option>
                          <option value="days">Days</option>
                        </select>
                        <button
                          type="button"
                          onClick={() => {
                            // Handle scheduled sending
                          }}
                          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                        >
                          Schedule
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <div className="flex justify-end space-x-3 mt-6">
                    <button
                      type="button"
                      onClick={() => setIsEditing(false)}
                      className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                    >
                      Save Changes
                    </button>
                  </div>
                </form>
              ) : (
                <div className="space-y-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="text-xl font-semibold text-gray-800">{selectedEvent.title}</h3>
                      <p className="text-gray-600 mt-1">{selectedEvent.description}</p>
                    </div>
                    <span className="px-3 py-1 text-sm rounded-full bg-red-100 text-red-800">
                      {selectedEvent.category}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center space-x-2">
                      <FaUser className="text-gray-400" />
                      <span className="text-gray-600">{selectedEvent.presenter}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <FaClock className="text-gray-400" />
                      <span className="text-gray-600">
                        {selectedEvent.date.toLocaleDateString()} at {selectedEvent.date.toLocaleTimeString()}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <FaUsers className="text-gray-400" />
                      <span className="text-gray-600">{selectedEvent.audience}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <FaCompass className="text-gray-400" />
                      <span className="text-gray-600">{selectedEvent.navigator}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <FaCalendar className="text-gray-400" />
                      <span className="text-gray-600">
                        Released: {selectedEvent.releaseDate ? selectedEvent.releaseDate.toLocaleDateString() : 'Not Released'}
                      </span>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Agenda:</h4>
                    <div className="space-y-2">
                      {selectedEvent.agenda.map((item, index) => (
                        <div key={index} className="flex items-center space-x-4">
                          <span className="text-gray-600 font-medium min-w-[120px]">{item.time}</span>
                          <span className="text-gray-600">{item.description}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )
            ) : (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-medium text-gray-900">Event Questions</h3>
                </div>

                <div className="space-y-4">
                  {/* Sample questions - replace with actual data */}
                  <div className="bg-gray-50 dark:bg-gray-800/60 rounded-xl p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium text-gray-900">What should I prepare for the Q1 review?</p>
                        <p className="text-sm text-gray-500 mt-1">Asked by John Doe</p>
                      </div>
                      <span className="text-sm text-gray-500">2 days ago</span>
                    </div>
                  </div>

                  <div className="bg-gray-50 dark:bg-gray-800/60 rounded-xl p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium text-gray-900">Will there be a recording available?</p>
                        <p className="text-sm text-gray-500 mt-1">Asked by Jane Smith</p>
                      </div>
                      <span className="text-sm text-gray-500">1 day ago</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* New Event Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Create New Event</h2>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setNewEvent({ ...newEvent, isFeatured: !newEvent.isFeatured })}
                  className="p-2 text-yellow-400 hover:text-yellow-500"
                >
                  {newEvent.isFeatured ? (
                    <FaStar className="w-5 h-5" />
                  ) : (
                    <FaRegStar className="w-5 h-5" />
                  )}
                </button>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <FaTimes className="w-5 h-5" />
                </button>
              </div>
            </div>

            <form onSubmit={handleCreateEvent} className="space-y-4">
              {/* Event Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Event Type
                </label>
                <select
                  value={newEvent.eventType}
                  onChange={(e) => setNewEvent({ ...newEvent, eventType: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  required
                >
                  <option value="">Select an event type</option>
                  {/* Add event type options here */}
                </select>
              </div>

              {/* Presenter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Presenter
                </label>
                <select
                  value={newEvent.presenter}
                  onChange={(e) => setNewEvent({ ...newEvent, presenter: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  required
                >
                  <option value="">Select a presenter</option>
                  {/* Add presenter options here */}
                </select>
              </div>

              {/* Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date
                </label>
                <input
                  type="date"
                  value={newEvent.date}
                  onChange={(e) => setNewEvent({ ...newEvent, date: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  required
                />
              </div>

              {/* Time */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Time
                </label>
                <input
                  type="time"
                  value={newEvent.time}
                  onChange={(e) => setNewEvent({ ...newEvent, time: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  required
                />
              </div>

              {/* Audience */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Audience
                </label>
                <select
                  value={newEvent.audience}
                  onChange={(e) => setNewEvent({ ...newEvent, audience: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  required
                >
                  <option value="all">All Clients</option>
                  <option value="some">Some Clients</option>
                </select>
              </div>

              {/* Release Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Release Date
                </label>
                <input
                  type="date"
                  value={newEvent.releaseDate}
                  onChange={(e) => setNewEvent({ ...newEvent, releaseDate: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  required
                />
              </div>

              {/* Submit Button */}
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Create Event
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default UpcomingEvents; 